import i18next from 'i18next';
import _ from 'lodash';
import { useMemo } from 'react';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { isIndexing } from '@/utilities/datasources.utilities';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { containsIgnoreCase } from '@/utilities/utilities';
import { sqAgentsApi } from '@seeqdev/api/endpoint/AgentsApi';
import { AgentProvisioningStatusEnum } from '@seeqdev/api/model/AgentOrchestratorOutputV1';
import type { AgentOrchestratorOutputV1 } from '@seeqdev/api/model/AgentOrchestratorOutputV1';
import type { AgentStatusOutputV1 } from '@seeqdev/api/model/AgentStatusOutputV1';
import type { DatasourcesStatusOutputV1 } from '@seeqdev/api/model/DatasourcesStatusOutputV1';
import type { InstallerOutputV1 } from '@seeqdev/api/model/InstallerOutputV1';

export function getAgentFilterParameters(searchParams: Partial<AgentFilterParameters>): AgentFilterParameters {
  const filterParams: AgentFilterParameters = {
    name: '',
    version: '',
    status: '',
  };

  for (const key of Object.keys(filterParams) as Array<keyof AgentFilterParameters>) {
    if (key in searchParams) {
      const value = searchParams[key];
      if (Array.isArray(value)) {
        filterParams[key] = value.join(',');
      } else if (!_.isNil(value)) {
        filterParams[key] = String(value);
      }
    }
  }

  return filterParams;
}

export function filterAndSortProvisionedOrchestrators(
  agentOrchestrators: AgentOrchestratorOutputV1[],
  agentFilterParameters: AgentFilterParameters,
): AgentOrchestrator[] | null {
  if (_.isNil(agentOrchestrators)) {
    return null;
  }

  if (!_.isEmpty(agentFilterParameters.version)) {
    return [];
  }

  if (
    !_.isEmpty(agentFilterParameters.status) &&
    agentFilterParameters.status !== SeeqNames.Agents.Status.PreProvisioned
  ) {
    return [];
  }

  let filteredOrchestrators: AgentOrchestratorOutputV1[] = _.filter(
    agentOrchestrators,
    (orchestrator) => orchestrator.agentProvisioningStatus === AgentProvisioningStatusEnum.PREPROVISIONED,
  );

  filteredOrchestrators = _.filter(filteredOrchestrators, (orchestrator) =>
    containsIgnoreCase(orchestrator.name, agentFilterParameters.name),
  );

  filteredOrchestrators = _.sortBy(filteredOrchestrators, 'name');

  const agentOrchestratorMap: AgentOrchestrator[] = [];
  for (const orchestrator of filteredOrchestrators) {
    if (orchestrator.name) {
      agentOrchestratorMap.push({
        id: orchestrator.name,
        name: orchestrator.name,
        agents: [] as AgentStatusOutputV1[],
        provisionedAt: orchestrator.agentProvisioningRequestedAt,
        expiresAt: orchestrator.agentProvisioningExpirationAt,
      });
    }
  }
  return agentOrchestratorMap;
}

export function isIgnitionAgent(agent: AgentStatusOutputV1): boolean {
  return agent.name.startsWith(SeeqNames.Ignition.AgentName);
}

/**
 * Filters the agents by the filter parameters provided, groups them by agent orchestrator, and returns the agent
 * orchestrators in severity order (disconnected, then unknown, then connecting, then connected).
 */
export function filterAndSortAgents(
  agents: AgentStatusOutputV1[],
  filterParams: AgentFilterParameters,
): AgentOrchestrator[] | null {
  if (_.isNil(agents)) {
    return null;
  }

  const preFilterOrchestrators = getPreFilterAgentOrchestrators(agents);

  let filteredAgents = agents;

  if (!_.isEmpty(filterParams.name)) {
    filteredAgents = _.filter(filteredAgents, (agent) => containsIgnoreCase(agent.name, filterParams.name));
  }

  if (!_.isEmpty(filterParams.version)) {
    filteredAgents = _.filter(filteredAgents, (agent) => containsIgnoreCase(agent.version ?? '', filterParams.version));
  }

  if (!_.isEmpty(filterParams.status)) {
    filteredAgents = _.filter(filteredAgents, (agent) => agent.status === filterParams.status);
  }

  filteredAgents = _.sortBy(filteredAgents, 'name');

  const agentOrchestratorMap = new Map<string, AgentOrchestrator>();

  for (const agent of filteredAgents) {
    const orchestratorName = agent.orchestratorName;
    if (orchestratorName) {
      const matchingOrchestrator = agentOrchestratorMap.get(orchestratorName);
      if (matchingOrchestrator) {
        matchingOrchestrator.agents.push(agent);
        if (isIgnitionAgent(agent)) {
          if (!matchingOrchestrator.ignitionVersion && agent.version) {
            matchingOrchestrator.ignitionVersion = agent.version;
          }
        } else {
          if (!matchingOrchestrator.version && agent.version) {
            matchingOrchestrator.version = agent.version;
          }
        }
      } else {
        agentOrchestratorMap.set(orchestratorName, {
          id: orchestratorName,
          name: orchestratorName,
          version: !isIgnitionAgent(agent) && agent.version ? agent.version : undefined,
          ignitionVersion: isIgnitionAgent(agent) && agent.version ? agent.version : undefined,
          status: preFilterOrchestrators?.get(orchestratorName)?.status ?? AgentStatus.Unknown,
          agents: [agent],
        });
      }
    }
  }

  for (const orchestratorName of agentOrchestratorMap.keys()) {
    const allAgents = preFilterOrchestrators?.get(orchestratorName)?.agents ?? [];
    const filteredAgents = agentOrchestratorMap.get(orchestratorName)?.agents ?? [];
    const entry = agentOrchestratorMap.get(orchestratorName);
    if (entry) {
      entry.someAgentsFilteredOut = allAgents.length > filteredAgents.length;
    }
  }

  let filteredOrchestrators = [...agentOrchestratorMap.values()];
  const disconnectedOrchestrators = _.sortBy(
    _.filter(filteredOrchestrators, (orchestrator) => orchestrator.status === AgentStatus.Disconnected),
    'name',
  );
  filteredOrchestrators = _.difference(filteredOrchestrators, disconnectedOrchestrators);
  const unknownOrchestrators = _.sortBy(
    _.filter(filteredOrchestrators, (orchestrator) => orchestrator.status === AgentStatus.Unknown),
    'name',
  );
  filteredOrchestrators = _.difference(filteredOrchestrators, unknownOrchestrators);
  const connectingOrchestrators = _.sortBy(
    _.filter(filteredOrchestrators, (orchestrator) => orchestrator.status === AgentStatus.Connecting),
    'name',
  );
  filteredOrchestrators = _.difference(filteredOrchestrators, connectingOrchestrators);
  const connectedOrchestrators = _.sortBy(
    _.filter(filteredOrchestrators, (orchestrator) => orchestrator.status === AgentStatus.Connected),
    'name',
  );

  return _.concat(disconnectedOrchestrators, unknownOrchestrators, connectingOrchestrators, connectedOrchestrators);
}

export function getPreFilterAgentOrchestrators(agents: AgentStatusOutputV1[]): Map<string, AgentOrchestrator> | null {
  if (_.isNil(agents)) {
    return null;
  }

  const agentOrchestratorMap = new Map<string, AgentOrchestrator>();
  for (const agent of agents) {
    const orchestratorName = agent.orchestratorName;
    if (orchestratorName) {
      if (agentOrchestratorMap.has(orchestratorName)) {
        const existing = agentOrchestratorMap.get(orchestratorName);
        if (existing) {
          existing.agents.push(agent);
          existing.status = getNewAgentOrchestratorStatus(
            existing.status ?? AgentStatus.Unknown,
            getAgentStatus(agent),
          );
        }
      } else {
        agentOrchestratorMap.set(orchestratorName, {
          id: orchestratorName,
          name: orchestratorName,
          version: agent.version ?? '',
          status: getAgentStatus(agent),
          agents: [agent],
        });
      }
    }
  }
  return agentOrchestratorMap;
}

export function getAgentOrchestratorStatuses(agents: AgentStatusOutputV1[]): Map<string, AgentStatus> | null {
  if (_.isNil(agents)) {
    return null;
  }

  const nameToStatusMap = new Map<string, AgentStatus>();
  for (const agent of agents) {
    const orchestratorName = agent.orchestratorName;
    if (orchestratorName) {
      if (nameToStatusMap.has(orchestratorName)) {
        const currentStatus = nameToStatusMap.get(orchestratorName) ?? AgentStatus.Unknown;
        nameToStatusMap.set(orchestratorName, getNewAgentOrchestratorStatus(currentStatus, getAgentStatus(agent)));
      } else {
        nameToStatusMap.set(orchestratorName, getAgentStatus(agent));
      }
    }
  }
  return nameToStatusMap;
}

export function countConnectedAgentOrchestrators(nameToStatusMap: Map<string, AgentStatus>): number {
  if (_.isNil(nameToStatusMap)) {
    return 0;
  }

  return _.filter([...nameToStatusMap.values()], (status) => status === AgentStatus.Connected).length;
}

export function getRemoteAgentOrchestrators(agents: AgentStatusOutputV1[]) {
  if (_.isNil(agents)) {
    return [];
  }

  const remoteAgents = _.filter(agents, (agent) => agent.remoteAgent);
  const agentOrchestratorMap = new Map<string, AgentOrchestrator>();
  for (const agent of remoteAgents) {
    const orchestratorName = agent.orchestratorName;
    if (orchestratorName) {
      const matchingOrchestrator = agentOrchestratorMap.get(orchestratorName);
      if (matchingOrchestrator) {
        matchingOrchestrator.agents.push(agent);
        if (isIgnitionAgent(agent)) {
          if (!matchingOrchestrator.ignitionVersion && agent.version) {
            matchingOrchestrator.ignitionVersion = agent.version;
          }
        } else {
          if (!matchingOrchestrator.version && agent.version) {
            matchingOrchestrator.version = agent.version;
          }
        }
      } else {
        agentOrchestratorMap.set(orchestratorName, {
          id: orchestratorName,
          name: orchestratorName,
          version: !isIgnitionAgent(agent) && agent.version ? agent.version : undefined,
          ignitionVersion: isIgnitionAgent(agent) && agent.version ? agent.version : undefined,
          agents: [agent],
          remoteUpdateStatus: agent.remoteUpdateStatus,
          remoteUpdateError: agent.remoteUpdateError,
        });
      }
    }
  }

  return _.sortBy([...agentOrchestratorMap.values()], 'name');
}

const getNewAgentOrchestratorStatus = (currentOrchestratorStatus: AgentStatus, newAgentStatus: AgentStatus) => {
  if (_.includes([currentOrchestratorStatus, newAgentStatus], AgentStatus.Disconnected)) {
    return AgentStatus.Disconnected;
  } else if (_.includes([currentOrchestratorStatus, newAgentStatus], AgentStatus.Unknown)) {
    return AgentStatus.Unknown;
  } else if (_.includes([currentOrchestratorStatus, newAgentStatus], AgentStatus.Connecting)) {
    return AgentStatus.Connecting;
  } else if (_.includes([currentOrchestratorStatus, newAgentStatus], AgentStatus.Connected)) {
    return AgentStatus.Connected;
  } else {
    return AgentStatus.Unknown;
  }
};

export function getDatasourcesCountOfOrchestrator(
  datasources: DatasourcesStatusOutputV1,
  agentOrchestrator: AgentOrchestrator,
): number {
  if (_.isNil(agentOrchestrator)) {
    return 0;
  }
  const agents = getOrchestratorDatasourceCountsByAgent(datasources, agentOrchestrator) ?? new Map();
  return _.sum([...agents.values()]);
}

export function getOrchestratorDatasourceCountsByAgent(
  datasources: DatasourcesStatusOutputV1,
  agentOrchestrator: AgentOrchestrator,
): Map<AgentStatusOutputV1, number> | null {
  if (_.isNil(agentOrchestrator)) {
    return null;
  }

  const agents = agentOrchestrator.agents ?? [];
  return new Map<AgentStatusOutputV1, number>(
    agents.map((agent) => [agent, getDatasourcesCountOfAgent(datasources, agent)]),
  );
}

export function getDatasourcesCountOfAgent(datasources: DatasourcesStatusOutputV1, agent: AgentStatusOutputV1) {
  if (_.isNil(agent)) {
    return 0;
  }

  return getDatasourcesServedByAgent(datasources, agent).length;
}

export function getIndexingDatasourcesCountOfOrchestrator(
  datasources: DatasourcesStatusOutputV1,
  agentOrchestrator: AgentOrchestrator,
) {
  if (_.isNil(agentOrchestrator)) {
    return 0;
  }

  const agents = agentOrchestrator.agents ?? [];
  return _.sum(_.map(agents, (agent) => getIndexingDatasourcesCountOfAgent(datasources, agent)));
}

export function getIndexingDatasourcesCountOfAgent(datasources: DatasourcesStatusOutputV1, agent: AgentStatusOutputV1) {
  if (_.isNil(agent)) {
    return 0;
  }

  return _.filter(getDatasourcesServedByAgent(datasources, agent), (datasource) => isIndexing(datasource)).length;
}

export function getRemainingIndexingTimeOfOrchestrator(
  datasources: DatasourcesStatusOutputV1,
  agentOrchestrator: AgentOrchestrator,
) {
  if (_.isNil(agentOrchestrator)) {
    return null;
  }

  const agents = agentOrchestrator.agents ?? [];
  const max = _.max(_.map(agents, (agent) => getRemainingIndexingTimeOfAgent(datasources, agent)));
  return max ?? undefined;
}

export function getRemainingIndexingTimeOfAgent(datasources: DatasourcesStatusOutputV1, agent: AgentStatusOutputV1) {
  if (_.isNil(agent)) {
    return null;
  }

  return _.max(_.map(getDatasourcesServedByAgent(datasources, agent), 'remainingIndexingDuration'));
}

const getDatasourcesServedByAgent = (datasources: DatasourcesStatusOutputV1, agent: AgentStatusOutputV1) => {
  if (!agent?.name) {
    return [];
  }
  return _.filter(
    datasources.datasources,
    (datasource) => !_.isEmpty(_.filter(datasource.connections, (connection) => connection.agentName === agent.name)),
  );
};

export function getAgentStatus(agent: AgentStatusOutputV1) {
  if (_.isNil(agent)) {
    return AgentStatus.Unknown;
  }

  if (agent.status === SeeqNames.Connectors.Connections.Status.Disconnected) {
    return AgentStatus.Disconnected;
  } else if (agent.status === SeeqNames.Connectors.Connections.Status.Connecting) {
    return AgentStatus.Connecting;
  } else if (agent.status === SeeqNames.Connectors.Connections.Status.Connected) {
    return AgentStatus.Connected;
  } else {
    return AgentStatus.Unknown;
  }
}

export async function archiveAgent(agent: AgentStatusOutputV1) {
  try {
    await sqAgentsApi.archiveAgent({ agentName: agent.name });
    successToast({
      messageKey: 'ADMIN.AGENTS.SUCCESSFULLY_ARCHIVED',
      messageParams: { name: agent.name },
    });
  } catch (error) {
    errorToast({ httpResponseOrError: error });
  }
}

export async function restartAgent(agent: AgentStatusOutputV1) {
  try {
    await sqAgentsApi.restartAgent({ agentName: agent.name });
    successToast({
      messageKey: 'ADMIN.AGENTS.RESTART_SUCCESS',
      messageParams: { name: agent.name },
    });
  } catch (error) {
    errorToast({ httpResponseOrError: error });
  }
}

export function formatInstallers(installers: InstallerOutputV1[]): VersionOption[] {
  if (_.isNil(installers)) {
    return [];
  }
  return _.map(installers, (installer) => {
    const flag = installer.flag ?? '';
    const marketingName = installer.marketingName ?? '';
    const installerName = installer.name ?? '';
    return {
      label: flag.includes('LOW_DISK_SPACE')
        ? i18next.t(`ADMIN.AGENTS.${flag}`, {
            installerName: marketingName,
          })
        : marketingName,
      value: installerName,
      isDisabled: flag.includes('LOW_DISK_SPACE'),
      downloadFlag: flag.includes('RETRIEVABLE'),
    };
  });
}

export function getVersionOptionOfStagedInstaller(
  options: VersionOption[],
  stageInstaller?: string,
): VersionOption | null | undefined {
  if (!stageInstaller) {
    return null;
  }

  return _.find(options, (option) => option.value === stageInstaller) ?? undefined;
}

export function sourceAddressIsInvalid(sourceAddress: string) {
  // Should be kept in sync with backend validation in
  // https://github.com/seeq12/crab/blob/develop/appserver/query/src/main/java/com/seeq/appserver/query/v1/agents/PreProvisionInputV1.kt
  const sourceAddressRegex =
    /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])(\/(3[0-2]|[1-2][0-9]|[0-9]))?$/;
  return !(_.isEmpty(sourceAddress) || sourceAddressRegex.test(sourceAddress));
}

export async function preProvisionNewAgent(
  machineName: string,
  agentName: string,
  sourceAddress: string,
  reProvisioningAllowed: boolean,
): Promise<string> {
  const preProvisionInput = {
    machineName,
    agentName: agentName.length > 0 ? agentName : undefined,
    sourceAddress: sourceAddress.length > 0 ? sourceAddress : undefined,
    reProvisioningAllowed,
  };
  const preProvisionOutput = await sqAgentsApi.preProvision(preProvisionInput);
  return preProvisionOutput.data.oneTimePassword ?? '';
}

/**
 * Compares two version strings in either format "<major>.<minor>.<patch>" or "R<major>.<minor>.<patch>-<timestamp".
 *  The timestamp is ignored. Null/empty versions are considered to be the newest version so they do not interfer
 *  with collective version status checks.
 *
 * returns:
 * a negative number if version1 < version2,
 * a positive number if version1 > version2,
 * 0 if they are equal or if either version is null/undefined.
 *
 *  @param version1 - The first version string to compare.
 *  @param version2 - The second version string to compare.
 */
export function compareVersions(version1: string | undefined, version2: string | undefined): number {
  if (!version1 && !version2) {
    return 0;
  } else if (!version1) {
    return 1;
  } else if (!version2) {
    return -1;
  }

  // Normalize versions by removing the "R" prefix and ignoring the timestamp if present
  version1 = version1.replace(/^R?/, '').split('-')[0];
  version2 = version2.replace(/^R?/, '').split('-')[0];

  // Match the version format "<major>.<minor>.<patch>"
  const regex = /^(\d+)\.(\d+)\.(\d+).*$/;
  const match1 = version1.match(regex);
  const match2 = version2.match(regex);
  if (_.isNil(match1) || _.isNil(match2) || match1.length !== 4 || match2.length !== 4) {
    return 0; // Invalid version format
  }
  const [major1, minor1, patch1] = match1.slice(1).map(Number);
  const [major2, minor2, patch2] = match2.slice(1).map(Number);

  // Compare major, minor, and patch versions
  if (major1 !== major2) {
    return major1 - major2;
  }
  if (minor1 !== minor2) {
    return minor1 - minor2;
  }
  return patch1 - patch2; // Compare patch if all else is equal
}

/**
 * Compares the age of two version strings with timestamps in the format "R<major>.<minor>.<patch>-<timestamp>".
 * Returns:
 * the age in days of the current version compared to the latest version
 * 1 if the current version is older than the latest version
 * 0 if the current version is the same or newer than the latest version
 *
 * @param currentVersion - The current version string with timestamp format "R<major>.<minor>.<patch>-vYYYYMMDDTTTT"
 * @param latestVersion - The latest version string with timestamp format "R<major>.<minor>.<patch>-vYYYYMMDDTTTT"
 * @returns The age in days (0 if same or newer, Number.MAX_SAFE_INTEGER for invalid input or format)
 */
function compareVersionAge(currentVersion: string, latestVersion: string): number {
  if (_.isNil(currentVersion) || _.isNil(latestVersion)) {
    return Number.MAX_SAFE_INTEGER;
  }

  // Extract the timestamp part of the version string
  const currentVersionTimestamp = currentVersion.split('-')[1];
  const latestVersionTimestamp = latestVersion.split('-')[1];

  if (_.isNil(currentVersionTimestamp) || _.isNil(latestVersionTimestamp)) {
    return Number.MAX_SAFE_INTEGER;
  }

  // Derive the date components from the timestamp and create a Date object from them
  const regex = /^v(\d{4})(\d{2})(\d{2})(\d{4})/; // Matches "vYYYYMMDDTTTT"
  const currentVersionMatch = currentVersionTimestamp.match(regex);
  const latestVersionMatch = latestVersionTimestamp.match(regex);
  if (
    _.isNil(currentVersionMatch) ||
    _.isNil(latestVersionMatch) ||
    currentVersionMatch.length !== 5 ||
    latestVersionMatch.length !== 5
  ) {
    return Number.MAX_SAFE_INTEGER; // Invalid timestamp format
  }
  const [__, year1, month1, day1, currentVersionTime] = currentVersionMatch;
  const [___, year2, month2, day2, latestVersionTime] = latestVersionMatch;
  const currentVersionDate = new Date(Date.UTC(+year1, +month1 - 1, +day1));
  const latestVersionDate = new Date(Date.UTC(+year2, +month2 - 1, +day2));

  const semanticComparison = compareVersions(currentVersion, latestVersion);

  if (currentVersionDate > latestVersionDate) {
    return 0;
  } else if (currentVersionDate.getTime() === latestVersionDate.getTime()) {
    if (semanticComparison < 0) {
      return 1;
    }
    return +currentVersionTime >= +latestVersionTime ? 0 : 1;
  } else {
    const msInDay = 24 * 60 * 60 * 1000;
    const ageInDays = Math.floor((latestVersionDate.getTime() - currentVersionDate.getTime()) / msInDay);
    if (ageInDays <= 0 && semanticComparison < 0) {
      return 1;
    }
    return ageInDays;
  }
}

/**
 * Compares the versions of two agent orchestrators.
 * @param agentOrchestrator1
 * @param agentOrchestrator2
 */
function compareAgentOrchestratorVersions(
  agentOrchestrator1: AgentOrchestrator,
  agentOrchestrator2: AgentOrchestrator,
): number {
  if (_.isNil(agentOrchestrator1) || _.isNil(agentOrchestrator2)) {
    return 0;
  }
  return compareVersions(agentOrchestrator1.version?.trim() ?? '', agentOrchestrator2.version?.trim() ?? '');
}

/**
 * Checks the version status of a collection of agent orchestrators against the provided versions.
 * @param agentOrchestrators
 * @param versions
 */
export function checkAgentsCollectiveVersionStatus(
  agentOrchestrators: AgentOrchestrator[],
  versions: VersionOption[],
): number {
  if (_.isEmpty(agentOrchestrators) || _.isEmpty(versions)) {
    return 0;
  }

  let oldestAgentAge = 0;
  for (const orchestrator of agentOrchestrators) {
    const age = checkSingleAgentVersionStatus(orchestrator.version, versions);
    if (age > oldestAgentAge) {
      oldestAgentAge = age;
    }
  }
  return oldestAgentAge;
}

/**
 * Checks the version status of a single agent orchestrator against the provided versions.
 *
 * Returns:
 * - 0: If the agent version is up to date (same as or newer than the latest available version)
 * - 1: If the agent version is semantically older but has the same timestamp date as the latest
 * - N (positive integer): The number of days the agent version is behind the latest available version
 * - Number.MAX_SAFE_INTEGER: If the agent's version is not found in the available versions,
 *   if provided bad input (null/undefined checkVersion or empty versions array),
 *   or if the version timestamp format is invalid
 *
 * @param checkVersion
 * @param versions a sorted array of available versions, with the latest version at index 0
 */
export function checkSingleAgentVersionStatus(checkVersion: string | undefined, versions: VersionOption[]): number {
  if (_.isNil(checkVersion) || _.isEmpty(checkVersion.trim()) || _.isEmpty(versions)) {
    return Number.MAX_SAFE_INTEGER;
  }

  const latestAvailableVersion = versions[0];

  // We find the matching available version in order to get the timestamp for comparison
  let agentVersion = versions.find((version) => version.value.includes(checkVersion?.trim()));

  // Special handling for irregular versions
  // 100.1.20063.34200 -> 2024-12-06
  if (agentVersion && agentVersion.label.trim() === 'R100.1.20063.34200') {
    agentVersion.label = 'R100.1.20063.34200-v202412060000';
  }

  //  Handle unknown versions
  if (_.isNil(agentVersion)) {
    const regex = /.*v(\d{4})(\d{2})(\d{2})(\d{4}).*/; // Matches "vYYYYMMDDTTTT"
    if (checkVersion?.trim().match(regex)) {
      // If the version has a datetime field, use it
      agentVersion = {
        label: checkVersion?.trim(),
        value: '',
        downloadFlag: false,
        isDisabled: false,
      };
    } else {
      // Try to find the closest, older semantic version and assign that age to this version
      for (const version of versions) {
        const comparisonResult = compareVersions(checkVersion?.trim(), version.label);
        if (comparisonResult > 0 && version.label.trim().match(regex)) {
          agentVersion = version;
          break; // Break out of the loop
        }
      }
    }
  }

  if (_.isNil(agentVersion)) {
    // No matching or older version found, consider it severely out of date
    return Number.MAX_SAFE_INTEGER;
  }

  const isNewerVersion = compareVersions(agentVersion.label, latestAvailableVersion.label) >= 0;
  if (isNewerVersion) {
    return 0;
  } else {
    // Determine how out of date the version is based on the timestamp
    return compareVersionAge(agentVersion.label, latestAvailableVersion.label);
  }
}

/**
 * Finds the version info of the oldest agent orchestrator (by version) from the provided array of agent orchestrators.
 * @param agentOrchestrators
 */
export function findOldestAgentVersion(agentOrchestrators: AgentOrchestrator[]): AgentOrchestrator {
  if (_.isNil(agentOrchestrators) || _.isEmpty(agentOrchestrators)) {
    return {
      id: '',
      name: '',
      version: '',
      agents: [],
    } as AgentOrchestrator;
  }

  // Sort the agent orchestrators by version
  return [...agentOrchestrators].sort(compareAgentOrchestratorVersions)[0];
}

/**
 * Checks if any of the provided agent orchestrators are currently downloading or extracting an update.
 * @param agentOrchestrators
 */
export function checkForActiveDownloads(agentOrchestrators: AgentOrchestrator[]): boolean {
  if (agentOrchestrators.some((orchestrator) => orchestrator.remoteUpdateStatus === 'Pending Update')) {
    return true;
  }
  return agentOrchestrators.some((orchestrator) => {
    return ['Downloading', 'Extracting', 'Retrieving'].some((prefix) => {
      return orchestrator.remoteUpdateStatus?.startsWith(prefix);
    });
  });
}

/**
 * Sorts the provided agent orchestrators array in place based on the provided sort state
 * @param agentOrchestrators
 * @param sortState
 */
export function sortAgentOrchestratorsPerTable(agentOrchestrators: AgentOrchestrator[], sortState: SortState): void {
  agentOrchestrators.sort((a, b) => {
    const direction = sortState.sortAsc ? 1 : -1;
    switch (sortState.sortBy) {
      case 'name':
        return direction * a.name.localeCompare(b.name);
      case 'version':
        return direction * compareVersions(a.version?.trim() ?? '', b.version?.trim() ?? '');
      case 'remoteUpdateStatus':
        return direction * (a.remoteUpdateStatus ?? '').localeCompare(b.remoteUpdateStatus ?? '');
      default:
        return 0; // No sorting applied
    }
  });
}

/**
 * Counts the number of agent orchestrators that are older than the provided target version.
 * @param agentOrchestrators
 * @param targetVersion
 */
export function getOldAgentCount(agentOrchestrators: AgentOrchestrator[], targetVersion: string): number {
  if (_.isEmpty(agentOrchestrators) || _.isNil(targetVersion)) {
    return 0;
  }

  return agentOrchestrators.filter(
    (orchestrator) => compareVersions(orchestrator.version?.trim() ?? '', targetVersion) < 0,
  ).length;
}

/**
 * Returns a translation key based on the version age of an agent in days.
 * @param versionAgeInDays - The age of the agent version in days
 * @param t - The i18next translation function
 */
export function getAgentVersionStatusMessage(
  agentOrchestrators: AgentOrchestrator[],
  versions: VersionOption[],
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (_.isEmpty(agentOrchestrators) || _.isEmpty(versions)) {
    return t('ADMIN.AGENTS.UPDATE_AGENTS.STATUS.UP_TO_DATE');
  }

  let oldestAgentAge = 0;
  let pendingOutOfSupportCount = 0;
  let outOfSupportCount = 0;

  agentOrchestrators.forEach((orchestrator) => {
    const age = checkSingleAgentVersionStatus(orchestrator.version, versions);
    if (age > oldestAgentAge) {
      oldestAgentAge = age;
    }

    if (age >= OUT_OF_SUPPORT_THRESHOLD) {
      outOfSupportCount += 1;
    } else if (age >= PENDING_OUT_OF_SUPPORT_THRESHOLD) {
      pendingOutOfSupportCount += 1;
    }
  });

  if (outOfSupportCount === 0 && pendingOutOfSupportCount === 0) {
    if (oldestAgentAge > 0) {
      return t('ADMIN.AGENTS.UPDATE_AGENTS.STATUS.OUTDATED');
    }
    return t('ADMIN.AGENTS.UPDATE_AGENTS.STATUS.UP_TO_DATE');
  }

  const messageParts: string[] = [];

  if (outOfSupportCount > 0) {
    messageParts.push(t('ADMIN.AGENTS.UPDATE_AGENTS.STATUS.UNSUPPORTED', { count: outOfSupportCount }));
  }

  if (pendingOutOfSupportCount > 0) {
    messageParts.push(t('ADMIN.AGENTS.UPDATE_AGENTS.STATUS.PENDING', { count: pendingOutOfSupportCount }));
  }

  const statusMessage = `${messageParts.join(t('ADMIN.AGENTS.UPDATE_AGENTS.STATUS.AND'))}.`;

  // Use different message based on whether there are unsupported agents
  let ctaMessage: string;
  if (outOfSupportCount > 0) {
    const linkText = t('ADMIN.AGENTS.UPDATE_AGENTS.PLEASE_UPDATE_LINK_TEXT');
    const link = `<a href="https://support.seeq.com/kb/latest/cloud/updating-remote-agents-from-seeq-server" target="_blank" rel="noopener">${linkText}</a>`;
    ctaMessage = t('ADMIN.AGENTS.UPDATE_AGENTS.PLEASE_UPDATE', { link });
  } else {
    const linkText = t('ADMIN.AGENTS.UPDATE_AGENTS.PLEASE_UPDATE_PENDING_ONLY_LINK_TEXT');
    const link = `<a href="https://support.seeq.com/kb/latest/cloud/updating-remote-agents-from-seeq-server" target="_blank" rel="noopener">${linkText}</a>`;
    ctaMessage = t('ADMIN.AGENTS.UPDATE_AGENTS.PLEASE_UPDATE_PENDING_ONLY', { link });
  }

  return `${statusMessage} ${ctaMessage}`;
}

/**
 * Collectively represents all software agents running on a single remote agent machine
 */
export interface AgentOrchestrator {
  id: string;
  name: string;
  version?: string;
  ignitionVersion?: string;
  seeqLinkSdkVersion?: string;
  status?: AgentStatus;
  agents: AgentStatusOutputV1[];
  remoteUpdateStatus?: string;
  remoteUpdateError?: string;
  someAgentsFilteredOut?: boolean;
  provisionedAt?: string;
  expiresAt?: string;
}

/**
 * Convert an AgentStatusOutputV1 to an AgentOrchestrator
 * @param agentStatusOutput
 */
export function convertAgentStatusOutputToAgentOrchestrator(agentStatusOutput: AgentStatusOutputV1): AgentOrchestrator {
  return {
    id: agentStatusOutput.orchestratorName,
    name: agentStatusOutput.orchestratorName,
    version: agentStatusOutput.version,
    status: getAgentStatus(agentStatusOutput),
    agents: [agentStatusOutput],
  } as AgentOrchestrator;
}

/**
 * Represents an agent installer available for download
 */
export interface VersionOption {
  value: string;
  label: string;
  isDisabled: boolean;
  downloadFlag: boolean;
}

export interface AgentFilterParameters {
  name: string;
  version: string;
  status: string;
}

/**
 * Serializes agent filter parameters to URLSearchParams for use in URL query strings.
 * Empty strings are omitted for cleaner URLs.
 */
export function serializeAgentFiltersToUrl(filters: Partial<AgentFilterParameters>): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.name) params.set('name', filters.name);
  if (filters.version) params.set('version', filters.version);
  if (filters.status) params.set('status', filters.status);

  return params;
}

/**
 * Deserializes URLSearchParams to AgentFilterParameters with default values.
 * Returns a complete AgentFilterParameters object with defaults for missing values.
 */
export function deserializeAgentFiltersFromUrl(searchParams: URLSearchParams): AgentFilterParameters {
  return {
    name: searchParams.get('name') || '',
    version: searchParams.get('version') || '',
    status: searchParams.get('status') || '',
  };
}

/**
 * Represents the current sort state of the agent orchestrators table in the AgentUpdate modal
 */
export interface SortState {
  sortBy: string;
  sortAsc: boolean;
}

export enum AgentStatus {
  Connected = 'Connected',
  Connecting = 'Connecting',
  Disconnected = 'Disconnected',
  Unknown = 'Unknown',
}

export const agentStatusIconMap = new Map([
  [AgentStatus.Unknown, 'fa-exclamation-triangle sq-status-error width-17'],
  [AgentStatus.Disconnected, 'fa-exclamation-triangle sq-status-error width-17'],
  [AgentStatus.Connecting, 'fa-circle-notch fa-spin sq-status-progress width-17'],
  [AgentStatus.Connected, 'fa-check-circle sq-status-good width-17'],
]);

/**
 * Reflects the severity of an agent's version status compared to the latest available version.
 */
export enum AgentVersionSeverity {
  UpToDate = 0,
  OutOfDate = 1,
  Warning = 2,
  Critical = 3,
}

/**
 * Extracted function for ease of testing
 * Determines the number of out of support and pending out of support remote agents based on their versions and the
 * available installers. We only consider connected, non-Ignition remote agents.
 * @param installers
 * @param agentStatuses
 */
export function getOutOfSupport(installers: VersionOption[], agentStatuses: AgentStatusOutputV1[] | undefined) {
  let outOfSupportCount = 0;
  let pendingOutOfSupportCount = 0;
  if (!_.isNil(installers) && installers.length > 0 && !_.isNil(agentStatuses) && agentStatuses.length > 0) {
    const agentOrchestrators = getRemoteAgentOrchestrators(agentStatuses);
    for (const agentOrchestrator of agentOrchestrators) {
      if (
        agentOrchestrator.agents.some(
          (agent) => !isIgnitionAgent(agent) && agent.status === SeeqNames.Connectors.Connections.Status.Connected,
        )
      ) {
        const versionAge = checkSingleAgentVersionStatus(agentOrchestrator.version, installers);
        if (versionAge >= OUT_OF_SUPPORT_THRESHOLD) {
          outOfSupportCount += 1;
        } else if (versionAge >= PENDING_OUT_OF_SUPPORT_THRESHOLD) {
          pendingOutOfSupportCount += 1;
        }
      }
    }
  }
  return { outOfSupportCount, pendingOutOfSupportCount };
}

/**
 * Determines the number of out of support and pending out of support remote agents based on their versions and the
 * available installers.
 * @param installers
 * @param agentStatuses
 */
export function useOutOfSupport(installers: VersionOption[], agentStatuses: AgentStatusOutputV1[] | undefined) {
  return useMemo(() => {
    return getOutOfSupport(installers, agentStatuses);
  }, [installers, JSON.stringify(agentStatuses)]);
}

export function generateVersionTimestamp(daysAgo: number) {
  const date = new Date(new Date().getTime() - daysAgo * 24 * 60 * 60 * 1000);
  return `${date.toISOString().split('T')[0].replace(/-/g, '')}0000`;
}

export function generateAgentUpdateKBLink(linkText: string) {
  return `<a href="https://telemetry.seeq.com/support-link/kb/latest/cloud/updating-remote-agents-from-seeq-server" target="_blank" rel="noopener">${linkText}</a>`;
}

export const OUT_OF_SUPPORT_THRESHOLD = 365; // days
export const PENDING_OUT_OF_SUPPORT_THRESHOLD = 275; // days (365 - 90)
export const ZERO_DAYS_AGO = generateVersionTimestamp(0);
export const PENDING_OUT_OF_SUPPORT_DAYS_AGO = generateVersionTimestamp(PENDING_OUT_OF_SUPPORT_THRESHOLD);
export const OUT_OF_SUPPORT_DAYS_AGO = generateVersionTimestamp(OUT_OF_SUPPORT_THRESHOLD);
